/**
 * settingsStore - the single source of truth for the genuinely new user
 * settings surfaced by the ESC pause menu (src/ui/settingsMenu.js): master
 * volume, mouse/look sensitivity, movement speed, and the environment-sound
 * toggles. Settings that already have a home elsewhere (graphics quality,
 * movement mode, display name, mic/camera) are NOT stored here - the menu drives
 * their existing APIs directly.
 *
 * Pure, framework-free module (mirrors src/chat.js / src/movementMode.js): the
 * only side effects are a versioned JSON blob in localStorage under
 * `oggi_settings` and an `oggi:settings-changed` CustomEvent on window, which
 * mirrors the existing `oggi:quality-changed` broadcast (src/main.js). Lives in
 * src/utils/ so it sits inside the jsconfig typecheck scope and is trivially
 * Vitest-testable.
 *
 * Every default equals the app's pre-existing behavior, so boot-time consumers
 * can read the store unconditionally without changing anything until the user
 * actually moves a slider.
 */

const STORAGE_KEY = 'oggi_settings';
const SCHEMA_VERSION = 1;

/**
 * @typedef {'masterVolume'|'mouseSensitivity'|'moveSpeedMultiplier'|'droneSound'|'videoScreenSound'|'controlScheme'|'cameraView'|'videoGridCollapsed'|'showHotspots'|'showMinimap'|'uiSounds'|'ambienceSound'|'spatialAudio'|'huddles'} SettingKey
 */

/**
 * @typedef {number|boolean|string} SettingValue
 */

/**
 * @typedef {Object} SettingSpec
 * @property {'number'|'boolean'|'enum'} type
 * @property {SettingValue} default
 * @property {number} [min] - inclusive lower bound (number settings)
 * @property {number} [max] - inclusive upper bound (number settings)
 * @property {string[]} [values] - allowed values (enum settings)
 */

/**
 * The full schema. Adding a key here is all that's needed to make it
 * persisted, clamped, and typed; unknown keys are rejected everywhere.
 * @type {Record<SettingKey, SettingSpec>}
 */
export const SETTING_SPECS = {
  // Environment-audio master gain. 1 = untouched (fans out to drone SFX +
  // video-screen <video> volumes).
  masterVolume: { type: 'number', default: 1, min: 0, max: 1 },
  // Look sensitivity multiplier. Applied as `angularSensibility = 600 / value`
  // on desktop and as a lookSpeed multiplier on touch. 1 = current behavior.
  mouseSensitivity: { type: 'number', default: 1, min: 0.25, max: 2 },
  // Walk-speed multiplier over the platform base (0.2 desktop / 0.5 touch).
  moveSpeedMultiplier: { type: 'number', default: 1, min: 0.5, max: 2 },
  // Drone hover SFX. Off by default, matching DroneAudioController's default.
  droneSound: { type: 'boolean', default: false },
  // Video-screen audio. On by default (no forced mute).
  videoScreenSound: { type: 'boolean', default: true },
  // Desktop control scheme: 'classic' = WASD + pointer-lock + crosshair,
  // 'hybrid' = WASD + drag-to-look + direct cursor clicks, 'easy' =
  // click-to-walk + drag-to-look + direct cursor clicks. Touch devices ignore
  // this (src/input/controlScheme.js resolves them to 'touch').
  controlScheme: { type: 'enum', values: ['classic', 'hybrid', 'easy'], default: 'classic' },
  // First- or third-person view. 'first' = the historical eye camera; 'third'
  // = derived chase camera + visible self-pod (src/entities/ChaseCameraRig.js).
  cameraView: { type: 'enum', values: ['first', 'third'], default: 'first' },
  // Floating video grid collapsed to its "N on camera" pill (src/ui/videoGrid.js).
  videoGridCollapsed: { type: 'boolean', default: false },
  // World-space exhibit labels (src/ui/hotspots/hotspotOverlay.js).
  showHotspots: { type: 'boolean', default: true },
  // The corner minimap (src/ui/hud/levelMinimap.js).
  showMinimap: { type: 'boolean', default: true },
  // Synthesized interface micro-SFX (src/audio/uiSounds.js).
  uiSounds: { type: 'boolean', default: true },
  // Looping background ambience bed (src/audio/ambience.js).
  ambienceSound: { type: 'boolean', default: true },
  // 3D positional voice: HRTF/stereo panning per avatar (src/audio/spatialVoice.js).
  // Default-on is safe because spatialVoice falls back to the legacy flat path
  // whenever the WebAudio graph can't run (no context, unsupported speaker
  // routing, a track that exposes no MediaStreamTrack).
  spatialAudio: { type: 'boolean', default: true },
  // Huddle circles: stand close to form a semi-private voice group with a soft
  // dome (src/entities/HuddleManager.js). Default-on (the spatialAudio posture,
  // not the strict "default = pre-existing behavior" rule): enforcement is
  // receiver-side and degrades gracefully - turning it off simply makes the
  // local player hear everyone at normal volume and draws no dome.
  huddles: { type: 'boolean', default: true },
};

/** @returns {Record<string, SettingValue>} a fresh defaults object */
function defaults() {
  /** @type {Record<string, SettingValue>} */
  const out = {};
  for (const key of Object.keys(SETTING_SPECS)) {
    out[key] = SETTING_SPECS[/** @type {SettingKey} */ (key)].default;
  }
  return out;
}

/**
 * Coerce + clamp a raw value against a key's spec. Unknown key -> undefined.
 * Non-finite numbers and unknown enum values fall back to the spec default
 * rather than persisting garbage.
 * @param {string} key
 * @param {*} value
 * @returns {SettingValue|undefined}
 */
function coerce(key, value) {
  const spec = SETTING_SPECS[/** @type {SettingKey} */ (key)];
  if (!spec) return undefined;
  if (spec.type === 'boolean') return Boolean(value);
  if (spec.type === 'enum') {
    return spec.values.includes(/** @type {string} */ (value))
      ? /** @type {string} */ (value)
      : spec.default;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return /** @type {number} */ (spec.default);
  return Math.min(/** @type {number} */ (spec.max), Math.max(/** @type {number} */ (spec.min), n));
}

/** Safe localStorage read (missing/blocked storage -> null). @returns {string|null} */
function readRaw() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

/** Safe localStorage write (failures are swallowed - persistence is best-effort). @param {string} json */
function writeRaw(json) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* private mode / quota / disabled storage - keep the in-memory value */
  }
}

/**
 * Parse persisted settings, dropping anything malformed or out of range.
 * Corrupt JSON and wrong-shaped blobs recover silently to defaults.
 * @returns {Record<string, SettingValue>}
 */
function load() {
  const state = defaults();
  const raw = readRaw();
  if (!raw) return state;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return state;
    const values = parsed.settings && typeof parsed.settings === 'object' ? parsed.settings : {};
    for (const key of Object.keys(SETTING_SPECS)) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        const coerced = coerce(key, values[key]);
        if (coerced !== undefined) state[key] = coerced;
      }
    }
  } catch {
    /* corrupt JSON - fall through to defaults */
  }
  return state;
}

/** @type {Record<string, SettingValue>} */
let state = load();

/** @type {Set<(key: string, value: SettingValue) => void>} */
const listeners = new Set();

/** Persist the full state as a versioned blob. */
function persist() {
  writeRaw(JSON.stringify({ version: SCHEMA_VERSION, settings: state }));
}

/**
 * Notify in-process subscribers and broadcast the window CustomEvent that
 * boot-time consumers (camera, audio) listen for.
 * @param {string} key
 * @param {SettingValue} value
 */
function notify(key, value) {
  listeners.forEach((fn) => {
    try {
      fn(key, value);
    } catch (e) {
      console.warn('settingsStore listener failed:', e);
    }
  });
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('oggi:settings-changed', { detail: { key, value } }));
  }
}

/**
 * Read one setting's current value.
 * @param {SettingKey|string} key
 * @returns {SettingValue|undefined} undefined for unknown keys
 */
export function getSetting(key) {
  return state[key];
}

/** @returns {Record<string, SettingValue>} a snapshot copy of every setting */
export function getAllSettings() {
  return { ...state };
}

/**
 * Clamp, persist, and broadcast a new value. No-op (no persist, no notify) when
 * the key is unknown or the coerced value is unchanged.
 * @param {SettingKey|string} key
 * @param {SettingValue} value
 * @returns {SettingValue|undefined} the value actually stored
 */
export function setSetting(key, value) {
  const coerced = coerce(key, value);
  if (coerced === undefined) return undefined;
  if (state[key] === coerced) return coerced;
  state[key] = coerced;
  persist();
  notify(key, coerced);
  return coerced;
}

/**
 * Restore every setting to its default, persisting and notifying per changed
 * key (so live consumers reset without a reload).
 */
export function resetSettings() {
  const fresh = defaults();
  const changed = [];
  for (const key of Object.keys(fresh)) {
    if (state[key] !== fresh[key]) changed.push(key);
  }
  state = fresh;
  persist();
  changed.forEach((key) => notify(key, state[key]));
}

/**
 * Subscribe to setting changes. Fires once per changed key with (key, value).
 * @param {(key: string, value: SettingValue) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
